import React, { useEffect, useState } from 'react';
import {Table, Button, Modal, Space} from 'antd';
import './ServiceDetailPage.css';
import { DEPLOY_ENV } from "../utils/config";
import {ServiceInfoService} from "../http/ServiceInfoService";
import {toast} from "react-toastify";

interface ServiceDetailPageProps {
    appKey: string;
}

const ServiceDetailPage: React.FC<ServiceDetailPageProps> = ({ appKey }) => {
    const [servers, setServers] = useState<ServiceServerInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [markLoading, setMarkLoading] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [serverToDelete, setServerToDelete] = useState<ServiceServerInfo | null>(null);

    useEffect(() => {
        setLoading(true);
        // 请求服务器列表接口，传递 appKey 和 environment
        ServiceInfoService.listServers({ appKey, environment: DEPLOY_ENV }).then(response => {
            setLoading(false);
            if (response.isFailed()) {
                return;
            }
            setServers(response.data);
        }).catch(error => {
            setLoading(false);
            console.error(error);
            toast.error('获取服务器列表失败');
        });
    }, [appKey]);

    const markActive = async (server: ServiceServerInfo) => {
        setMarkLoading(true);
        await ServiceInfoService.markActive({ appKey, environment: DEPLOY_ENV, internalIp: server.internalIp }).then(response => {
            setMarkLoading(false);
            if (response.isFailed()) {
                return;
            }
            toast.success(`服务器 ${server.instanceName} 已标记为在线`);
            window.location.reload();
        }).catch(error => {
            setMarkLoading(false);
            console.error(error);
            toast.error('操作失败');
        })
    };

    const markInactive = async (server: ServiceServerInfo) => {
        setMarkLoading(true);
        await ServiceInfoService.markInactive({ appKey, environment: DEPLOY_ENV, internalIp: server.internalIp }).then(response => {
            setMarkLoading(false);
            if (response.isFailed()) {
                return;
            }
            toast.success(`服务器 ${server.instanceName} 已标记为下线`);
            window.location.reload();
        }).catch(error => {
            setMarkLoading(false);
            console.error(error);
            toast.error('操作失败');
        });
    };

    const showDeleteModal = (server: ServiceServerInfo) => {
        setServerToDelete(server);
        setIsModalVisible(true);
    };

    const deleteServer = async (server: ServiceServerInfo) => {
        setDeleteLoading(true);
        await ServiceInfoService.deleteServer({ appKey, environment: DEPLOY_ENV, internalIp: server.internalIp }).then(response => {
            setDeleteLoading(false);
            if (response.isFailed()) {
                return;
            }
            toast.success(`服务器 ${server.instanceName} 已删除`);
            window.location.reload();
        }).catch(error => {
            setDeleteLoading(false);
            console.error(error);
            toast.error('操作失败');
        });
    }

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('复制成功');
        }).catch(() => {
            toast.error('复制失败');
        });
    };

    const renderWithCopyButton = (text: string) => {
        return (
            <div className="text-cell">
                {text}
                {
                    text && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => copyToClipboard(text)}
                        >
                            复制
                        </Button>
                    )
                }
            </div>
        );
    }

    const renderActions = (text: string, server: ServiceServerInfo) => {
        return (
            <Space align="baseline">
                {
                    server.state === 'ACTIVE' ? (
                        <Button loading={markLoading} onClick={() => markInactive(server)}>
                            标记下线
                        </Button>
                    ) : (
                        <Button loading={markLoading} onClick={() => markActive(server)}>
                            标记在线
                        </Button>
                    )
                }
                <Button onClick={() => showDeleteModal(server)} danger>
                    删除
                </Button>
            </Space>
        );
    }

    const columns = [
        {
            title: '实例ID',
            dataIndex: 'instanceId',
            key: 'instanceId',
            render: (text: string) => renderWithCopyButton(text),
        },
        {
            title: '实例名称',
            dataIndex: 'instanceName',
            key: 'instanceName',
        },
        {
            title: '内网IP',
            dataIndex: 'internalIp',
            key: 'internalIp',
            render: (text: string) => renderWithCopyButton(text),
        },
        {
            title: '外网IP',
            dataIndex: 'externalIp',
            key: 'externalIp',
            render: (text: string) => renderWithCopyButton(text),
        },
        {
            title: '区域',
            dataIndex: 'regionId',
            key: 'regionId',
        },
        {
            title: '访问实例',
            key: 'instanceUrl',
            render: (text: string, server: ServiceServerInfo) => (
                <a href={`https://ecs-workbench.aliyun.com/?from=ecs&instanceType=ecs&instanceId=${server.instanceId}&language=zh-CN`}
                   target="_blank" rel="noopener noreferrer">访问</a>
            ),
        },
        {
            title: '操作',
            key: 'action',
            render: renderActions,
        },
    ];

    return (
        <div className="service-detail-page">
            <h2>节点列表</h2>
            <Table
                rowKey="id"
                dataSource={servers}
                columns={columns}
                loading={loading}
                pagination={false}
            />
            <Modal
                title="确认删除"
                open={isModalVisible}
                onOk={() => deleteServer(serverToDelete!)}
                onCancel={() => setIsModalVisible(false)}
                okText="删除"
                cancelText="取消"
                confirmLoading={deleteLoading}
            >
                <p>确定要删除服务器 {serverToDelete?.instanceName}@{serverToDelete?.internalIp} 吗？</p>
            </Modal>
        </div>
    );
};

export default ServiceDetailPage;
